import { BANNER_TYPES, BANNER_VARIANTS, LOCAL_STORAGE_KEYS } from '@/app.constants';
import { cn } from '@/utils';
import { X } from 'lucide-react';
import React from 'react';
import Icons from './Icons';
import useBannerVisibility from './useBannerVisibility';

interface iBanner {
  type?: keyof typeof BANNER_TYPES;
  variant: keyof typeof BANNER_VARIANTS;
  text?: string;
  actionText?: string;
  action?: () => void;
  bannerKey: string;
  className?: string;
  children?: React.ReactNode;
}

const backgroundClassMap: Record<string, string> = {
  DANGER: 'bg-danger_light',
  SUCCESS: 'bg-success_light',
  WARNING: 'bg-warning_light',
  INFO: 'bg-primary_lighter',
  NEUTRAL: 'bg-purple_light',
};

const Banner = ({
  type = BANNER_TYPES.NOTIFICATION as keyof typeof BANNER_TYPES,
  text,
  actionText,
  variant,
  action,
  bannerKey,
  className,
  children,
}: iBanner) => {
  const [isBannerVisible, setIsBannerVisible] = useBannerVisibility(bannerKey);
  
  const hasChildren = React.Children.count(children) > 0;

  const hideBanner = () => {
    const storedBannerData = localStorage.getItem(LOCAL_STORAGE_KEYS.BANNER_KEY);
    const localStorageBannerData = (storedBannerData && JSON.parse(storedBannerData)) || {};
    localStorage.setItem(LOCAL_STORAGE_KEYS.BANNER_KEY, JSON.stringify({ ...localStorageBannerData, [bannerKey]: false }));
    if (typeof setIsBannerVisible === 'function') {
      setIsBannerVisible(false);
    }
  };

  if (!isBannerVisible) return null;

  return (
    <div
      className={cn(`text-dark pe-8px txt-sub-rg banner ${className}`, backgroundClassMap[variant], {
        'flex items-start rounded-[8px]': type === BANNER_TYPES.NOTIFICATION,
        'flex items-center justify-center': type === BANNER_TYPES.BANNER,
        'flex items-between': hasChildren,
      })}
    >
      <div
        className={cn('flex items-center justify-between gap-2 p-2', {
          'w-full': type === BANNER_TYPES.NOTIFICATION,
          'flex p-3': hasChildren,
        })}
      >
        <div
          className={cn('flex gap-[10px]', {
            'items-center justify-center': !hasChildren,
          })}
        >
          {/* Icon */}
          <span><Icons variant={variant} size={18} /></span>

          {/* Content */}
          <div
            className={cn('flex gap-2 items-center', {
              'gap-0': hasChildren,
            })}
          >
            {hasChildren ? (
              <div className="flex flex-col">{children}</div>
            ) : (
              <>
                <div className='text-xs'>{text}</div>
                {actionText && (
                  <div
                    className="cursor-pointer text-xs text-primary"
                    onClick={() => {
                      if (action) action();
                    }}
                  >
                    {actionText}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Cross Button */}
      {type === BANNER_TYPES.BANNER && (
        <div
          className={cn('cursor-pointer flex items-center text-center m-2 ')}
          onClick={hideBanner}
        >
          <X size={16} />
        </div>
      )}
    </div>
  );
};

export default Banner;
